import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Clock, CheckCircle2, Circle, PlayCircle, StopCircle,
  Loader2, User, ChevronDown, ChevronUp, RefreshCw,
  AlertTriangle, Lock, FileText, Camera, X, CheckCheck,
  ArrowRight, Info, ImageIcon, ZoomIn, ChevronLeft, ChevronRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Aktuelle Berliner Uhrzeit als "HH:MM" String
function getBerlinTimeStr() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const h = parts.find(p => p.type === 'hour')?.value || '00';
  const m = parts.find(p => p.type === 'minute')?.value || '00';
  return `${h}:${m}`;
}

const PRIORITY_COLORS = {
  kritisch: 'border-l-red-500 bg-red-50/40 dark:bg-red-900/10',
  hoch:     'border-l-orange-500 bg-orange-50/30 dark:bg-orange-900/10',
  mittel:   'border-l-blue-500 bg-blue-50/20 dark:bg-blue-900/5',
  niedrig:  'border-l-gray-400 bg-gray-50/20 dark:bg-gray-900/5',
};

const SHIFT_GRADIENTS = [
  'from-orange-500 to-amber-400',
  'from-blue-500 to-cyan-400',
  'from-violet-500 to-purple-400',
  'from-emerald-500 to-green-400',
  'from-rose-500 to-pink-400',
];

export const SchichtView = ({ schichtId }) => {
  const [schicht, setSchicht] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locationId, setLocationId] = useState('');
  const [sessionLoading, setSessionLoading] = useState(false);
  const [completingId, setCompletingId] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);

  // Übergabe-Modal (Schicht beenden)
  const [showUbergabeModal, setShowUbergabeModal] = useState(false);
  const [ubergabeNotiz, setUbergabeNotiz] = useState('');
  const [ubergabeFoto, setUbergabeFoto] = useState(null);
  const [ubergabeFotoPreview, setUbergabeFotoPreview] = useState(null);
  const [ubergabeSaving, setUbergabeSaving] = useState(false);
  const fotoInputRef = useRef(null);

  // Vorgänger-Protokoll Modal (Schicht beginnen)
  const [vorProtokoll, setVorProtokoll] = useState(null);
  const [showVorProtokollModal, setShowVorProtokollModal] = useState(false);
  const [bestaetigenLoading, setBestaetigenLoading] = useState(false);
  const [vorProtokollChecked, setVorProtokollChecked] = useState(false);

  // Berliner Uhrzeit live (aktualisiert jede Minute)
  const [currentBerlinTime, setCurrentBerlinTime] = useState(getBerlinTimeStr());

  useEffect(() => {
    const timer = setInterval(() => setCurrentBerlinTime(getBerlinTimeStr()), 30000);
    return () => clearInterval(timer);
  }, []);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLocation = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/user/locations`, { headers });
      const locs = r.data || [];
      if (locs.length > 0) setLocationId(locs[0].id);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchLocation(); }, [fetchLocation]);

  useEffect(() => {
    if (schichtId && locationId) {
      fetchSchicht();
      fetchAufgaben();
    }
  }, [schichtId, locationId]);

  const fetchSchicht = async () => {
    try {
      const r = await axios.get(`${API}/tasks/schichten/${schichtId}`, { headers });
      setSchicht(r.data);
    } catch { /* silent */ }
  };

  const fetchAufgaben = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(
        `${API}/tasks/schicht/${schichtId}/aufgaben?location_id=${locationId}`,
        { headers }
      );
      setData(r.data);
    } catch (e) {
      if (!silent) toast.error('Aufgaben konnten nicht geladen werden');
    } finally { if (!silent) setLoading(false); }
  };

  // Prüft ob Vorgänger-Protokoll vorhanden (für "Schicht beginnen")
  const fetchVorProtokoll = async () => {
    try {
      const r = await axios.get(
        `${API}/tasks/schicht/${schichtId}/vorheriges-protokoll?location_id=${locationId}`,
        { headers }
      );
      return r.data;
    } catch { return null; }
  };

  const handleStartSessionClick = async () => {
    // Vor dem Starten: Gibt es ein Protokoll vom Vorgänger?
    const protokoll = await fetchVorProtokoll();
    if (protokoll && protokoll.ubergabe_notiz && protokoll.status !== 'bestaetigt') {
      setVorProtokoll(protokoll);
      setVorProtokollChecked(false);
      setShowVorProtokollModal(true);
    } else {
      // Direkt starten
      await doStartSession();
    }
  };

  const doStartSession = async () => {
    setSessionLoading(true);
    try {
      await axios.post(`${API}/tasks/schicht-session/start`, {
        schicht_config_id: schichtId,
        location_id: locationId,
      }, { headers });
      toast.success('Schicht begonnen!');
      setShowVorProtokollModal(false);
      fetchAufgaben();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Starten der Schicht');
    } finally { setSessionLoading(false); }
  };

  const handleBestaetigenAndStart = async () => {
    if (!vorProtokoll?.id) return;
    setBestaetigenLoading(true);
    try {
      await axios.post(`${API}/tasks/schicht-session/bestaetigen`, {
        session_id: vorProtokoll.id,
      }, { headers });
      await doStartSession();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally { setBestaetigenLoading(false); }
  };

  // Schicht beenden: erst Abschluss-Aufgaben prüfen, dann Modal
  const handleEndSessionClick = () => {
    if (!data?.session?.id) return;
    if (!data.can_end_shift) {
      toast.error(`Bitte erst alle ${data.closing_tasks_offen} Abschluss-Aufgabe(n) erledigen!`);
      return;
    }
    setUbergabeNotiz('');
    setUbergabeFoto(null);
    setUbergabeFotoPreview(null);
    setShowUbergabeModal(true);
  };

  const handleFotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUbergabeFoto(file);
    const url = URL.createObjectURL(file);
    setUbergabeFotoPreview(url);
  };

  const handleUbergabeSubmit = async () => {
    if (!ubergabeNotiz.trim()) {
      toast.error('Bitte eine Übergabe-Notiz eingeben');
      return;
    }
    setUbergabeSaving(true);
    try {
      let foto_path = null;

      // Foto hochladen falls vorhanden
      if (ubergabeFoto) {
        const fd = new FormData();
        fd.append('file', ubergabeFoto);
        const uploadRes = await axios.post(`${API}/tasks/ubergabe-foto/upload`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        foto_path = uploadRes.data.path;
      }

      await axios.post(`${API}/tasks/schicht-session/end`, {
        session_id: data.session.id,
        notiz: ubergabeNotiz.trim(),
        foto_path,
      }, { headers });

      toast.success('Schicht erfolgreich übergeben!');
      setShowUbergabeModal(false);
      fetchAufgaben();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Beenden der Schicht');
    } finally { setUbergabeSaving(false); }
  };

  const handleComplete = async (task) => {
    if (!data?.session?.id) {
      toast.error('Bitte zuerst "Schicht beginnen" drücken!');
      return;
    }
    setCompletingId(task.id);

    // Optimistisches Update: Sofort lokal als erledigt markieren (kein Scroll-Reset)
    const now = new Date().toISOString();
    setData(prev => {
      if (!prev) return prev;
      const updatedAufgaben = prev.aufgaben.map(t =>
        t.id === task.id
          ? { ...t, is_completed: true, completed_at: now, completed_by_name: 'Du' }
          : t
      );
      const erledigt = updatedAufgaben.filter(t => t.is_completed).length;
      const openingOffen = updatedAufgaben.filter(t => t.is_opening_task && !t.is_completed).length;
      const closingOffen = updatedAufgaben.filter(t => t.is_closing_task && !t.is_completed).length;
      return {
        ...prev,
        aufgaben: updatedAufgaben,
        erledigt,
        offen: updatedAufgaben.length - erledigt,
        opening_tasks_offen: openingOffen,
        closing_tasks_offen: closingOffen,
        can_end_shift: closingOffen === 0,
        can_start_shift: openingOffen === 0,
      };
    });

    try {
      await axios.post(`${API}/tasks/completions`, {
        template_id: task.id,
        location_id: locationId,
        shift_session_id: data.session.id,
        turnus_date: data?.datum,
      }, { headers });
      toast.success(task.is_opening_task ? 'Eröffnungs-Aufgabe erledigt!' : 'Aufgabe erledigt!');
      // Stille Hintergrund-Synchronisation (keine Scroll-Unterbrechung)
      fetchAufgaben({ silent: true });
    } catch (e) {
      // Bei Fehler: optimistisches Update zurückrollen
      toast.error(e.response?.data?.detail || 'Fehler');
      fetchAufgaben();
    } finally { setCompletingId(null); }
  };

  const handleUncomplete = async (task) => {
    setCompletingId(task.id);

    // Optimistisches Update: Sofort als offen markieren
    setData(prev => {
      if (!prev) return prev;
      const updatedAufgaben = prev.aufgaben.map(t =>
        t.id === task.id
          ? { ...t, is_completed: false, completed_at: null, completed_by_name: null }
          : t
      );
      const erledigt = updatedAufgaben.filter(t => t.is_completed).length;
      const openingOffen = updatedAufgaben.filter(t => t.is_opening_task && !t.is_completed).length;
      const closingOffen = updatedAufgaben.filter(t => t.is_closing_task && !t.is_completed).length;
      return {
        ...prev,
        aufgaben: updatedAufgaben,
        erledigt,
        offen: updatedAufgaben.length - erledigt,
        opening_tasks_offen: openingOffen,
        closing_tasks_offen: closingOffen,
        can_end_shift: closingOffen === 0,
        can_start_shift: openingOffen === 0,
      };
    });

    try {
      await axios.delete(
        `${API}/tasks/completions/${task.id}?location_id=${locationId}&turnus_date=${data?.datum || ''}`,
        { headers }
      );
      toast.success('Aufgabe wieder geöffnet');
      fetchAufgaben({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
      fetchAufgaben();
    } finally { setCompletingId(null); }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    try { return new Date(isoStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  const formatDateTime = (isoStr) => {
    if (!isoStr) return '';
    try { return new Date(isoStr).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  if (!locationId) {
    return (
      <DashboardLayout>
        <div className="p-6 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  // Sortierung: Eröffnung → chronologisch nach Ziel-Uhrzeit → ab_uhrzeit → Priorität
  const _sortTasks = (tasks) => {
    const prio = { kritisch: 0, hoch: 1, mittel: 2, niedrig: 3 };
    return [...tasks].sort((a, b) => {
      // 1. blocks_others-Aufgaben ganz oben (opt-in Blockade)
      if (a.blocks_others !== b.blocks_others) return a.blocks_others ? -1 : 1;
      // 2. Eröffnungsaufgaben vor normalen (nur visuell, blockiert nicht mehr per Default)
      if (a.is_opening_task !== b.is_opening_task) return a.is_opening_task ? -1 : 1;
      // 3. Zeitlich nach ziel_uhrzeit sortieren (wenn gesetzt), dann ab_uhrzeit, dann Prio
      const tzA = a.ziel_uhrzeit || a.ab_uhrzeit || '99:99';
      const tzB = b.ziel_uhrzeit || b.ab_uhrzeit || '99:99';
      if (tzA !== tzB) return tzA < tzB ? -1 : 1;
      return (prio[a.priority] ?? 2) - (prio[b.priority] ?? 2);
    });
  };
  const openTasks = _sortTasks(data?.aufgaben?.filter(t => !t.is_completed) || []);
  const doneTasks = data?.aufgaben?.filter(t => t.is_completed) || [];
  const closingTasksOffen = data?.closing_tasks_offen || 0;
  const openingTasksOffen = data?.opening_tasks_offen || 0;
  const blockingTasksOffen = data?.blocking_tasks_offen || 0;
  const canStartShift = data ? data.can_start_shift !== false : true;
  const progress = data?.total > 0 ? Math.round((data.erledigt / data.total) * 100) : 0;
  const gradientIdx = schicht ? Math.abs(schicht.name.charCodeAt(0)) % SHIFT_GRADIENTS.length : 0;
  const session = data?.session;   // ← wurde durch früheres Edit gelöscht → fix
  const sessionAktiv = session?.status === 'aktiv';
  const sessionBeendet = session?.status === 'beendet' || session?.status === 'bestaetigt';

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-4">

        {/* Schicht-Header */}
        {schicht && (
          <div className={`bg-gradient-to-r ${SHIFT_GRADIENTS[gradientIdx]} rounded-2xl p-5 text-white shadow-lg`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-sm font-bold opacity-80 mb-1">{schicht.kuerzel}</div>
                <h1 className="text-2xl font-bold">{schicht.name}</h1>
                <div className="flex items-center gap-1 mt-1 text-sm opacity-90">
                  <Clock className="h-4 w-4" />
                  <span>{schicht.startzeit} – {schicht.endzeit} Uhr</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button onClick={fetchAufgaben} className="p-2 bg-white/20 rounded-xl hover:bg-white/30 transition-colors">
                  <RefreshCw className="h-4 w-4" />
                </button>
                <div className="text-right">
                  <div className="font-mono font-bold text-xl leading-none">{currentBerlinTime}</div>
                  <div className="text-xs opacity-70">Uhr (DE)</div>
                </div>
              </div>
            </div>
            <div className="mt-3 text-sm opacity-80">
              {data?.datum
                ? new Date(data.datum + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                : new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
        )}

        {/* Session-Status & Buttons */}
        <Card className="shadow-sm">
          <CardContent className="p-4">
            {!session ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium text-foreground">Schicht noch nicht begonnen</p>
                    <p className="text-sm text-muted-foreground">
                      Klicke auf "Schicht beginnen", um mit den Aufgaben zu starten.
                    </p>
                  </div>
                  <Button
                    onClick={handleStartSessionClick}
                    disabled={sessionLoading}
                    data-testid="start-session-btn"
                    className="shrink-0 gap-2"
                  >
                    {sessionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                    Schicht beginnen
                  </Button>
                </div>
              </div>
            ) : sessionAktiv ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                      <p className="font-medium text-green-600 dark:text-green-400">Schicht läuft</p>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Begonnen {formatTime(session.begonnen_um)} · {session.begonnen_von_name}
                    </p>
                  </div>
                  <Button
                    onClick={handleEndSessionClick}
                    disabled={sessionLoading}
                    variant="outline"
                    data-testid="end-session-btn"
                    className="shrink-0 gap-2 border-orange-300 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                  >
                    {sessionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <StopCircle className="h-4 w-4" />}
                    Schicht beenden
                  </Button>
                </div>
                {closingTasksOffen > 0 && (
                  <div className="flex items-center gap-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-2.5 text-sm text-orange-700 dark:text-orange-300">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>{closingTasksOffen} Abschluss-Aufgabe(n) noch offen – Pflicht vor Schichtende</span>
                  </div>
                )}
                {openingTasksOffen > 0 && (
                  <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-2.5 text-sm text-blue-700 dark:text-blue-300">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>{openingTasksOffen} Eröffnungs-Aufgabe(n) – bitte zuerst erledigen</span>
                  </div>
                )}
                {blockingTasksOffen > 0 && (
                  <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-2.5 text-sm text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>{blockingTasksOffen} blockierende Aufgabe(n) noch offen – andere Aufgaben sind gesperrt bis erledigt</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Schicht übergeben</p>
                    <p className="text-sm text-muted-foreground">
                      {formatTime(session.begonnen_um)} – {formatTime(session.beendet_um)}
                      {session.beendet_von_name && ` · ${session.beendet_von_name}`}
                    </p>
                    {session.ubergabe_notiz && (
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        "{session.ubergabe_notiz}"
                      </p>
                    )}
                  </div>
                </div>
                {/* Nächste Person kann jetzt die Schicht beginnen */}
                <Button
                  onClick={handleStartSessionClick}
                  disabled={sessionLoading}
                  data-testid="start-next-session-btn"
                  className="w-full gap-2"
                  variant="outline"
                >
                  {sessionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                  Meine Schicht beginnen (Übergabe lesen)
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Session erforderlich – Hinweis wenn keine Session */}
        {!session && data?.total > 0 && (
          <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-700 dark:text-blue-300">
            <Lock className="h-4 w-4 shrink-0" />
            <span>Starte die Schicht, um Aufgaben erledigen zu können.</span>
          </div>
        )}

        {/* Fortschritt */}
        {data && data.total > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-foreground">{data.erledigt} / {data.total} erledigt</span>
              <span className="text-muted-foreground">{progress}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            {progress === 100 && sessionAktiv && (
              <p className="text-sm text-green-600 dark:text-green-400 font-medium text-center">
                Alle Aufgaben erledigt – Schicht kann jetzt übergeben werden!
              </p>
            )}
          </div>
        )}

        {/* Aufgaben-Liste */}
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : data?.total === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-10 text-center">
              <CheckCircle2 className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">Keine Aufgaben für diese Schicht heute.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {openTasks.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Offen ({openTasks.length})</h3>
                {openTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    isCompleting={completingId === task.id}
                    sessionAktiv={sessionAktiv}
                    currentTime={currentBerlinTime}
                    blockingTasksOffen={blockingTasksOffen}
                    onComplete={() => handleComplete(task)}
                    onUncomplete={null}
                  />
                ))}
              </div>
            )}
            {doneTasks.length > 0 && (
              <div className="space-y-2">
                <button
                  onClick={() => setShowCompleted(v => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide"
                  data-testid="toggle-completed"
                >
                  Erledigt ({doneTasks.length})
                  {showCompleted ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
                {showCompleted && doneTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    isCompleting={completingId === task.id}
                    sessionAktiv={sessionAktiv}
                    currentTime={currentBerlinTime}
                    onComplete={null}
                    onUncomplete={() => handleUncomplete(task)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── MODAL: Vorgänger-Protokoll (Vollsperre) ── */}
      {showVorProtokollModal && vorProtokoll && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-2xl p-5 text-white">
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6" />
                <div>
                  <h2 className="font-bold text-lg">Übergabe vom Vorgänger</h2>
                  <p className="text-sm opacity-80">
                    {vorProtokoll.beendet_von_name} · {formatDateTime(vorProtokoll.beendet_um)}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {/* Notiz */}
              <div className="space-y-1.5">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Notizen & Vorkommnisse</p>
                <div className="bg-muted/50 rounded-lg p-3 text-sm text-foreground leading-relaxed">
                  {vorProtokoll.ubergabe_notiz}
                </div>
              </div>

              {/* Foto */}
              {vorProtokoll.ubergabe_foto_path && (
                <div className="space-y-1.5">
                  <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Foto-Anhang</p>
                  <img
                    src={`${API}/tasks/ubergabe-foto/${vorProtokoll.ubergabe_foto_path}`}
                    alt="Übergabe-Foto"
                    className="w-full rounded-lg max-h-48 object-cover"
                  />
                </div>
              )}

              {/* Erledigte Abschluss-Aufgaben */}
              {vorProtokoll.closing_tasks_done?.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Abschluss-Aufgaben ({vorProtokoll.closing_tasks_done.length})
                  </p>
                  <div className="space-y-1.5">
                    {vorProtokoll.closing_tasks_done.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                        <span className="text-foreground">{t.name}</span>
                        <span className="text-muted-foreground ml-auto text-xs">
                          {t.completed_by_name} · {formatTime(t.completed_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bestätigungs-Checkbox */}
              <div
                className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                  vorProtokollChecked
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-muted/30 hover:border-primary/50'
                }`}
                onClick={() => setVorProtokollChecked(v => !v)}
                data-testid="protokoll-bestaetigen-checkbox"
              >
                <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                  vorProtokollChecked ? 'border-primary bg-primary' : 'border-muted-foreground'
                }`}>
                  {vorProtokollChecked && <CheckCheck className="h-3 w-3 text-white" />}
                </div>
                <div>
                  <p className="font-medium text-sm">Ich habe den Bericht gelesen und übernehme die Schicht.</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Pflichtfeld – kein Überspringen möglich</p>
                </div>
              </div>

              <Button
                onClick={handleBestaetigenAndStart}
                disabled={!vorProtokollChecked || bestaetigenLoading}
                data-testid="gelesen-uebernommen-btn"
                className="w-full gap-2"
              >
                {bestaetigenLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Gelesen & Übernommen – Schicht starten
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Übergabe-Protokoll (Schicht beenden) ── */}
      {showUbergabeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-t-2xl p-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <StopCircle className="h-6 w-6" />
                  <div>
                    <h2 className="font-bold text-lg">Schicht übergeben</h2>
                    <p className="text-sm opacity-80">Protokoll für den Nachfolger erstellen</p>
                  </div>
                </div>
                <button onClick={() => setShowUbergabeModal(false)} className="p-1 hover:bg-white/20 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {/* Notiz (Pflicht) */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-foreground">
                  Notizen & Vorkommnisse <span className="text-destructive">*</span>
                </label>
                <Textarea
                  placeholder="Was ist passiert? Besonderheiten, offene Punkte, Hinweise für den Nachfolger..."
                  value={ubergabeNotiz}
                  onChange={e => setUbergabeNotiz(e.target.value)}
                  rows={4}
                  data-testid="ubergabe-notiz-input"
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">Pflichtfeld – muss ausgefüllt werden</p>
              </div>

              {/* Foto (Optional) */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground">
                  Foto-Anhang <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                {ubergabeFotoPreview ? (
                  <div className="relative">
                    <img
                      src={ubergabeFotoPreview}
                      alt="Foto-Vorschau"
                      className="w-full rounded-lg max-h-40 object-cover"
                    />
                    <button
                      onClick={() => { setUbergabeFoto(null); setUbergabeFotoPreview(null); }}
                      className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fotoInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center gap-2 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                    data-testid="foto-upload-btn"
                  >
                    <Camera className="h-8 w-8" />
                    <span className="text-sm font-medium">Kamera / Galerie</span>
                    <span className="text-xs">Tippe hier zum Aufnehmen oder Auswählen</span>
                  </button>
                )}
                <input
                  ref={fotoInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFotoChange}
                  className="hidden"
                  data-testid="foto-file-input"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowUbergabeModal(false)} className="flex-1">
                  Abbrechen
                </Button>
                <Button
                  onClick={handleUbergabeSubmit}
                  disabled={!ubergabeNotiz.trim() || ubergabeSaving}
                  data-testid="ubergabe-submit-btn"
                  className="flex-1 gap-2"
                >
                  {ubergabeSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />}
                  Übergabe speichern
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

// ── Galerie-Button + Slideshow (Portal für iOS-Kompatibilität) ───────────────
function GalleryButton({ task, images }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const touchStart = useRef(null);
  const first = images[0];

  const prev = () => setCurrent(i => (i - 1 + images.length) % images.length);
  const next = () => setCurrent(i => (i + 1) % images.length);

  // Body-Scroll sperren wenn Viewer offen (iOS)
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [open]);

  // Swipe-Handling
  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 50) { dx < 0 ? next() : prev(); }
    touchStart.current = null;
  };

  // createPortal: rendert direkt am <body> – bypasses alle CSS-Transform Probleme
  const viewer = open ? createPortal(
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 2147483647, // max z-index
        backgroundColor: '#000000',
        display: 'flex', flexDirection: 'column',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* X-Button Header – groß für mobile */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 8px', flexShrink: 0 }}>
        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, fontWeight: 600, maxWidth: '65%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {task.name}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {images.length > 1 && (
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>{current + 1} / {images.length}</span>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            style={{
              width: 56, height: 56, minWidth: 56, borderRadius: '50%',
              background: 'rgba(255,255,255,0.18)', border: '2px solid rgba(255,255,255,0.3)',
              color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation', outline: 'none',
            }}
          >
            <X style={{ width: 26, height: 26, strokeWidth: 2.5 }} />
          </button>
        </div>
      </div>

      {/* Bild – zentriert */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 12px', overflow: 'hidden' }}>
        <img
          key={current} // Key-Change triggert re-render bei Bild-Wechsel
          src={images[current].url}
          alt={images[current].caption || `Schritt ${current + 1}`}
          style={{ maxWidth: '100%', maxHeight: 'calc(100dvh - 200px)', objectFit: 'contain', borderRadius: 12, display: 'block' }}
          onError={(e) => { e.currentTarget.alt = 'Bild konnte nicht geladen werden'; }}
        />
      </div>

      {/* Caption + Navigation */}
      <div style={{ flexShrink: 0, padding: '12px 16px 16px' }}>
        {images[current].caption && (
          <p style={{ textAlign: 'center', color: '#fff', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>
            {images[current].caption}
          </p>
        )}
        {images.length > 1 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
            <button type="button" onClick={prev}
              style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
              <ChevronLeft style={{ width: 32, height: 32, color: '#fff' }} />
            </button>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {images.map((_, i) => (
                <button key={i} type="button" onClick={() => setCurrent(i)}
                  style={{ width: i === current ? 24 : 10, height: 10, borderRadius: 5, background: i === current ? '#fff' : 'rgba(255,255,255,0.35)', border: 'none', cursor: 'pointer', transition: 'all 0.2s', padding: 0 }} />
              ))}
            </div>
            <button type="button" onClick={next}
              style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
              <ChevronRight style={{ width: 32, height: 32, color: '#fff' }} />
            </button>
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>Auf X tippen zum Schließen</p>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button type="button"
        onClick={() => { setCurrent(0); setOpen(true); }}
        className="mt-2 flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors group"
        data-testid={`task-gallery-btn-${task.id}`}
      >
        <div className="relative overflow-hidden rounded-lg border border-blue-200 dark:border-blue-800 w-16 h-12 shrink-0">
          <img src={first.url} alt="Anleitung" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
          {images.length > 1 && (
            <div className="absolute bottom-0 right-0 bg-black/60 text-white text-[9px] font-bold px-1 rounded-tl">
              1/{images.length}
            </div>
          )}
        </div>
        <span className="flex items-center gap-1">
          <ImageIcon className="h-3 w-3" />
          {images.length > 1 ? `${images.length} Anleitungsbilder` : 'Anschauungsbild öffnen'}
        </span>
      </button>
      {viewer}
    </>
  );
}

// ── Einzelne Aufgaben-Karte ──────────────────────────────────────────────────
function TaskCard({ task, isCompleting, sessionAktiv, currentTime, blockingTasksOffen, onComplete, onUncomplete }) {
  const priorityStyle = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.mittel;
  const isClosing = task.is_closing_task;
  const isOpening = task.is_opening_task;
  const isBlockingOther = task.blocks_others;  // Diese Aufgabe blockiert andere
  const [showImageFull, setShowImageFull] = useState(false);

  // Punkt 5: Visuelle Priorität: Überfällig (rot) > Erledigt (grün) > blocks_others (amber) > Opening (blau) > Normal
  const isOverdue = task.is_overdue && !task.is_completed;

  // Punkt 4: Blockiert durch blocks_others-Aufgaben (opt-in, keine automatische is_opening_task-Kopplung)
  const blockedByOther = !isBlockingOther && !task.is_completed && sessionAktiv && (blockingTasksOffen || 0) > 0;

  // Ab-Uhrzeit-Sperre (bleibt unverändert)
  const abUhrzeit = task.ab_uhrzeit;
  const isTimeReached = !abUhrzeit || (currentTime >= abUhrzeit);
  const isLockedByTime = !task.is_completed && !!abUhrzeit && !isTimeReached;

  // Klickbar: Session aktiv, Zeit OK, nicht durch blocks_others geblockt
  const isClickable = !isLockedByTime && !blockedByOther && sessionAktiv && !task.is_completed;

  return (
    <>
    <Card
      className={`border-l-4 shadow-sm transition-opacity ${
        task.is_completed
          ? 'border-l-green-500 dark:border-l-green-400'
          : isOverdue
          ? 'border-l-red-500 dark:border-l-red-400'  // Überfällig gewinnt immer
          : isBlockingOther
          ? 'border-l-amber-500 dark:border-l-amber-400'  // blockierende Aufgabe: amber
          : isOpening
          ? 'border-l-blue-500 dark:border-l-blue-400'
          : priorityStyle
      } ${task.is_completed ? 'opacity-60' : ''} ${isLockedByTime || blockedByOther ? 'opacity-70' : ''}`}
      data-testid={`task-item-${task.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <button
            onClick={task.is_completed ? onUncomplete : (isClickable ? onComplete : null)}
            disabled={isCompleting || (!isClickable && !task.is_completed)}
            data-testid={task.is_completed ? `undo-task-${task.id}` : `complete-task-${task.id}`}
            className={`mt-0.5 shrink-0 transition-transform active:scale-90 ${(!isClickable && !task.is_completed) ? 'cursor-not-allowed opacity-40' : ''}`}
          >
            {isCompleting ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : task.is_completed ? (
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            ) : isLockedByTime ? (
              <Lock className="h-6 w-6 text-blue-400" />
            ) : blockedByOther ? (
              <Lock className="h-6 w-6 text-amber-400" />
            ) : !sessionAktiv ? (
              <Lock className="h-6 w-6 text-muted-foreground/40" />
            ) : (
              <Circle className={`h-6 w-6 transition-colors ${isBlockingOther ? 'text-amber-500 hover:text-amber-700' : isOpening ? 'text-blue-400 hover:text-blue-600' : 'text-muted-foreground/50 hover:text-primary'}`} />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className={`font-medium text-foreground ${task.is_completed ? 'line-through text-muted-foreground' : ''}`}>
                {task.name}
                {isClosing && !task.is_completed && (
                  <span className="ml-2 text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 px-1.5 py-0.5 rounded font-medium">
                    Abschluss
                  </span>
                )}
                {isBlockingOther && !task.is_completed && (
                  <span className="ml-2 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 rounded font-medium">
                    Zuerst erledigen
                  </span>
                )}
                {isOpening && !isBlockingOther && !task.is_completed && (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 px-1.5 py-0.5 rounded font-medium">
                    Eröffnung
                  </span>
                )}
                {isOverdue && (
                  <span className="ml-2 text-xs bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 px-1.5 py-0.5 rounded font-medium">
                    Überfällig seit {task.ziel_uhrzeit}
                  </span>
                )}
              </h3>
            </div>
            {task.description && (
              <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
            )}

            {/* Anschauungsbilder – Galerie/Slideshow */}
            {(() => {
              const imgs = task.reference_images?.length
                ? task.reference_images
                : task.reference_image_url
                  ? [{ url: task.reference_image_url, caption: '', index: 0 }]
                  : [];
              if (!imgs.length) return null;
              return (
                <GalleryButton task={task} images={imgs} />
              );
            })()}

            {/* Ab-Uhrzeit-Hinweis */}
            {isLockedByTime && (
              <div className="flex items-center gap-1.5 mt-2 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg px-2.5 py-1.5">
                <Lock className="h-3 w-3 shrink-0" />
                <span>Verfügbar ab <strong>{abUhrzeit} Uhr</strong> · Jetzt: {currentTime} Uhr</span>
              </div>
            )}

            {/* Eröffnungs-Aufgaben-Sperre (jetzt: blocks_others Opt-in) */}
            {blockedByOther && (
              <div className="flex items-center gap-1.5 mt-2 text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded-lg px-2.5 py-1.5">
                <Lock className="h-3 w-3 shrink-0" />
                <span>Warte auf "Zuerst erledigen"-Aufgaben</span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
              {task.ziel_uhrzeit && !task.is_completed && !isOverdue && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-3 w-3" /> Ziel: {task.ziel_uhrzeit}
                </span>
              )}
              {task.deadline_time && !task.is_completed && (
                <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                  <Clock className="h-3 w-3" /> Frist: {task.deadline_time}
                </span>
              )}
              {abUhrzeit && !isLockedByTime && !task.is_completed && (
                <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                  <Clock className="h-3 w-3" /> ab {abUhrzeit}
                </span>
              )}
              {task.is_completed && task.completed_by_name && (
                <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <User className="h-3 w-3" /> {task.completed_by_name}
                  {task.completed_at && ` · ${new Date(task.completed_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Vollbild-Modal für Anschauungsbild */}
    {showImageFull && task.reference_image_url && (
      <div
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
        onClick={() => setShowImageFull(false)}
        data-testid={`task-image-fullscreen-${task.id}`}
      >
        <div className="relative max-w-full max-h-full" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => setShowImageFull(false)}
            className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 z-10 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <p className="text-center text-white/70 text-sm mb-2">{task.name}</p>
          <img
            src={task.reference_image_url}
            alt="Anschauungsbild"
            className="max-w-[90vw] max-h-[80vh] object-contain rounded-xl"
          />
          <p className="text-center text-white/50 text-xs mt-2">Tippe außerhalb zum Schließen</p>
        </div>
      </div>
    )}
    </>
  );
}

export default SchichtView;
